import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Car, Plus, Trash2, Edit2, Upload, X, Star, ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';
import SEO from '../components/SEO';
import ShareButtons from '../components/ShareButtons';
import { toast } from 'sonner';
import { formatError } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BRANDS = ['Citroen', 'Opel', 'Fiat', 'Silence', 'XEV', 'Microlino', 'Carver', 'Romet', 'Jiayuan', 'RKS', 'Diğer'];

function getPhotoUrl(path) {
  if (!path) return null;
  return path.startsWith('http') ? path : `${API}/files/${path}`;
}

function VehicleForm({ onSubmit, initial, submitting, onDeleteExisting, onSetCoverExisting }) {
  const [form, setForm] = useState(initial || { brand: '', model: '', category: 'L6e', year: new Date().getFullYear(), custom_name: '', description: '' });
  const [newFiles, setNewFiles] = useState([]); // File[]
  const [newPreviews, setNewPreviews] = useState([]); // dataURL[]
  const fileInputRef = useRef(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const existingPhotos = initial?.photos || (initial?.photo_path ? [initial.photo_path] : []);
  const coverPath = initial?.photo_path || existingPhotos[0] || null;

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const valid = files.filter(f => {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: 10MB'dan büyük, atlandı`); return false; }
      return true;
    });
    if (!valid.length) return;
    valid.forEach(file => {
      const reader = new FileReader();
      reader.onload = ev => setNewPreviews(prev => [...prev, ev.target.result]);
      reader.readAsDataURL(file);
    });
    setNewFiles(prev => [...prev, ...valid]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeNewPhoto = (idx) => {
    setNewFiles(prev => prev.filter((_, i) => i !== idx));
    setNewPreviews(prev => prev.filter((_, i) => i !== idx));
  };

  return (
    <form onSubmit={e => { e.preventDefault(); onSubmit(form, newFiles); }} className="space-y-4">
      {/* Photos manager */}
      <div>
        <Label>Araç Fotoğrafları</Label>
        <div className="mt-1 grid grid-cols-3 gap-2" data-testid="garage-photos-grid">
          {/* Existing photos (edit mode) */}
          {existingPhotos.map((path) => (
            <div key={path} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 bg-slate-50 group" data-testid={`existing-photo-${path.split('/').pop()}`}>
              <img src={getPhotoUrl(path)} alt="" className="w-full h-full object-cover" />
              {coverPath === path && (
                <div className="absolute top-1 left-1 bg-amber-400 text-amber-900 text-[10px] font-bold px-1.5 py-0.5 rounded flex items-center gap-0.5">
                  <Star className="w-2.5 h-2.5 fill-amber-900" /> Kapak
                </div>
              )}
              <div className="absolute inset-0 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 bg-black/50 transition-opacity">
                {coverPath !== path && onSetCoverExisting && (
                  <button type="button" onClick={() => onSetCoverExisting(path)} className="bg-amber-400 hover:bg-amber-500 text-amber-900 p-1.5 rounded" title="Kapak yap" data-testid={`set-cover-${path.split('/').pop()}`}>
                    <Star className="w-3.5 h-3.5" />
                  </button>
                )}
                {onDeleteExisting && (
                  <button type="button" onClick={() => onDeleteExisting(path)} className="bg-red-500 hover:bg-red-600 text-white p-1.5 rounded" title="Sil" data-testid={`del-existing-${path.split('/').pop()}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
          {/* New uploads previews */}
          {newPreviews.map((src, idx) => (
            <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border border-emerald-300 bg-emerald-50 group" data-testid={`new-preview-${idx}`}>
              <img src={src} alt="" className="w-full h-full object-cover" />
              <div className="absolute top-1 left-1 bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">Yeni</div>
              <button type="button" onClick={() => removeNewPhoto(idx)} className="absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {/* Add tile */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="aspect-square rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 hover:border-emerald-400 hover:bg-emerald-50 transition-colors flex flex-col items-center justify-center gap-1 text-slate-500"
            data-testid="garage-photo-picker"
          >
            <Upload className="w-5 h-5" />
            <span className="text-xs font-medium">Fotoğraf Ekle</span>
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileChange}
          data-testid="garage-photo-input"
        />
        <p className="text-xs text-slate-400 mt-1.5">PNG, JPG · maks. 10MB · birden fazla seçebilirsiniz</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Marka</Label>
          <Input value={form.brand} onChange={e => set('brand', e.target.value)} placeholder="Marka" className="mt-1" data-testid="garage-brand-input" required />
        </div>
        <div>
          <Label>Model</Label>
          <Input value={form.model} onChange={e => set('model', e.target.value)} placeholder="Model" className="mt-1" data-testid="garage-model-input" required />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Kategori</Label>
          <Select value={form.category} onValueChange={v => set('category', v)}>
            <SelectTrigger className="mt-1" data-testid="garage-category-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="L6e">L6e (max 45 km/s)</SelectItem>
              <SelectItem value="L7e">L7e (max 90 km/s)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Model Yılı</Label>
          <Input type="number" value={form.year} onChange={e => set('year', parseInt(e.target.value))} min="2000" max="2030" className="mt-1" data-testid="garage-year-input" required />
        </div>
      </div>
      <div>
        <Label>Araç Adı / Lakap (isteğe bağlı)</Label>
        <Input value={form.custom_name} onChange={e => set('custom_name', e.target.value)} placeholder="Örn: 'Yeşil Şimşek'" className="mt-1" data-testid="garage-nickname-input" />
      </div>
      <div>
        <Label>Açıklama (isteğe bağlı)</Label>
        <Textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="Aracınız hakkında notlar..." rows={3} className="mt-1 resize-none" data-testid="garage-desc-input" />
      </div>
      <Button type="submit" disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="garage-vehicle-submit">
        {submitting ? 'Kaydediliyor...' : initial ? 'Güncelle' : 'Garaja Ekle'}
      </Button>
    </form>
  );
}

export default function GaragePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user === undefined) return; // still loading
    if (!user) { navigate('/giris'); return; }
    fetchGarage();
  }, [user]);

  const fetchGarage = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/garage`, { withCredentials: true });
      setVehicles(r.data.vehicles || []);
    } finally {
      setLoading(false);
    }
  };

  const uploadPhotos = async (vehicleId, files) => {
    let latest = null;
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      const up = await axios.post(`${API}/garage/${vehicleId}/upload-photo`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      latest = up.data;
    }
    return latest; // { path, photos, cover }
  };

  const handleAdd = async (form, newFiles) => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/garage`, form, { withCredentials: true });
      let vehicle = r.data.vehicle;
      vehicle.photos = vehicle.photos || [];
      if (newFiles && newFiles.length) {
        const last = await uploadPhotos(vehicle._id, newFiles);
        if (last) vehicle = { ...vehicle, photos: last.photos, photo_path: last.cover };
      }
      setVehicles(prev => [vehicle, ...prev]);
      setShowAdd(false);
      toast.success(`Araç garaja eklendi${newFiles?.length ? ` (${newFiles.length} fotoğraf)` : ''}!`);
    } catch (err) { toast.error(formatError(err)); }
    finally { setSubmitting(false); }
  };

  const handleEdit = async (form, newFiles) => {
    setSubmitting(true);
    try {
      await axios.put(`${API}/garage/${editingVehicle._id}`, form, { withCredentials: true });
      let patch = { ...form };
      if (newFiles && newFiles.length) {
        const last = await uploadPhotos(editingVehicle._id, newFiles);
        if (last) { patch.photos = last.photos; patch.photo_path = last.cover; }
      }
      setVehicles(prev => prev.map(v => v._id === editingVehicle._id ? { ...v, ...patch } : v));
      setEditingVehicle(null);
      toast.success('Araç güncellendi!');
    } catch (err) { toast.error(formatError(err)); }
    finally { setSubmitting(false); }
  };

  const handleDeletePhoto = async (vehicleId, path) => {
    try {
      const r = await axios.post(`${API}/garage/${vehicleId}/delete-photo`, { path }, { withCredentials: true });
      setVehicles(prev => prev.map(v => v._id === vehicleId ? { ...v, photos: r.data.photos, photo_path: r.data.cover } : v));
      // editing dialog state senk
      setEditingVehicle(ev => ev && ev._id === vehicleId ? { ...ev, photos: r.data.photos, photo_path: r.data.cover } : ev);
      toast.success('Fotoğraf kaldırıldı');
    } catch (err) { toast.error(formatError(err)); }
  };

  const handleSetCover = async (vehicleId, path) => {
    try {
      await axios.post(`${API}/garage/${vehicleId}/set-cover`, { path }, { withCredentials: true });
      setVehicles(prev => prev.map(v => v._id === vehicleId ? { ...v, photo_path: path } : v));
      setEditingVehicle(ev => ev && ev._id === vehicleId ? { ...ev, photo_path: path } : ev);
      toast.success('Kapak güncellendi');
    } catch (err) { toast.error(formatError(err)); }
  };

  const handleDelete = async (vehicleId) => {
    if (!window.confirm('Aracı garajdan kaldırmak istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API}/garage/${vehicleId}`, { withCredentials: true });
      setVehicles(prev => prev.filter(v => v._id !== vehicleId));
      toast.success('Araç kaldırıldı');
    } catch (err) { toast.error(formatError(err)); }
  };

  const [galleryVehicle, setGalleryVehicle] = useState(null);
  const [galleryIdx, setGalleryIdx] = useState(0);
  const openGallery = (vehicle, startIdx = 0) => {
    setGalleryVehicle(vehicle);
    setGalleryIdx(startIdx);
  };

  if (!user) return null;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO title="Garajım" noindex />
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Outfit, sans-serif' }}>Garajım</h1>
          <p className="text-slate-500 mt-1">Araçlarınızı yönetin</p>
        </div>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-vehicle-btn">
              <Plus className="w-4 h-4 mr-2" /> Araç Ekle
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle>Araç Ekle</DialogTitle></DialogHeader>
            <div className="mt-2">
              <VehicleForm onSubmit={handleAdd} submitting={submitting} />
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-10 h-10 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
        </div>
      ) : vehicles.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
          <Car className="w-16 h-16 text-slate-200 mx-auto mb-4" />
          <h3 className="font-bold text-slate-700 mb-2">Garajınız boş</h3>
          <p className="text-slate-500 mb-6">İlk aracınızı ekleyerek başlayın</p>
          <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setShowAdd(true)}>
            <Plus className="w-4 h-4 mr-2" /> İlk Aracımı Ekle
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {vehicles.map(vehicle => {
            const photos = vehicle.photos && vehicle.photos.length ? vehicle.photos : (vehicle.photo_path ? [vehicle.photo_path] : []);
            const cover = vehicle.photo_path || photos[0];
            return (
            <div key={vehicle._id} className="card-hover overflow-hidden group" data-testid={`garage-vehicle-${vehicle._id}`}>
              {/* Photo */}
              <div className="aspect-video bg-slate-100 relative overflow-hidden rounded-t-2xl">
                {getPhotoUrl(cover) ? (
                  <button
                    type="button"
                    onClick={() => openGallery(vehicle, 0)}
                    className="w-full h-full block cursor-zoom-in"
                    data-testid={`open-gallery-${vehicle._id}`}
                  >
                    <img src={getPhotoUrl(cover)} alt={vehicle.model} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setEditingVehicle(vehicle)}
                    className="w-full h-full flex flex-col items-center justify-center gap-2"
                  >
                    <Car className="w-12 h-12 text-slate-200" />
                    <span className="text-xs text-slate-400">Fotoğraf ekle</span>
                  </button>
                )}
                {/* Category badge */}
                <div className="absolute top-2 left-2 pointer-events-none">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${vehicle.category === 'L6e' ? 'bg-emerald-500 text-white' : 'bg-cyan-500 text-white'}`}>
                    {vehicle.category}
                  </span>
                </div>
                {/* Photo count badge */}
                {photos.length > 1 && (
                  <button
                    type="button"
                    onClick={() => openGallery(vehicle, 0)}
                    className="absolute top-2 right-2 bg-black/60 hover:bg-black/80 text-white text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-1 transition-colors"
                    data-testid={`photo-count-${vehicle._id}`}
                  >
                    <ImageIcon className="w-3 h-3" /> +{photos.length - 1}
                  </button>
                )}
                {/* Thumbnail strip */}
                {photos.length > 1 && (
                  <div className="absolute bottom-2 left-2 right-2 flex gap-1 overflow-x-auto scrollbar-hide">
                    {photos.slice(0, 5).map((p, idx) => (
                      <button
                        key={p}
                        type="button"
                        onClick={(e) => { e.stopPropagation(); openGallery(vehicle, idx); }}
                        className={`w-10 h-10 rounded border-2 overflow-hidden shrink-0 ${p === cover ? 'border-emerald-400' : 'border-white/70'}`}
                      >
                        <img src={getPhotoUrl(p)} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                    {photos.length > 5 && (
                      <div className="w-10 h-10 rounded bg-black/60 text-white flex items-center justify-center text-xs font-bold shrink-0">+{photos.length - 5}</div>
                    )}
                  </div>
                )}
              </div>

              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    {vehicle.custom_name && (
                      <div className="text-xs text-emerald-600 font-medium mb-0.5">"{vehicle.custom_name}"</div>
                    )}
                    <h3 className="font-bold text-slate-800">{vehicle.brand} {vehicle.model}</h3>
                    <p className="text-sm text-slate-400">{vehicle.year}</p>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => setEditingVehicle(vehicle)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                      data-testid={`edit-vehicle-${vehicle._id}`}
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(vehicle._id)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      data-testid={`delete-vehicle-${vehicle._id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
                {vehicle.description && (
                  <p className="text-xs text-slate-500 mt-2 line-clamp-2">{vehicle.description}</p>
                )}
                {user?._id && (vehicle.photos?.length || vehicle.photo_path) && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <ShareButtons
                      size="xs"
                      url={`${window.location.origin}/profil/${user._id}`}
                      title={`${vehicle.brand} ${vehicle.model}`}
                      text={`${vehicle.custom_name ? `"${vehicle.custom_name}" ` : ''}${vehicle.brand} ${vehicle.model} · Garajımdan`}
                    />
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editingVehicle} onOpenChange={() => setEditingVehicle(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Aracı Düzenle</DialogTitle></DialogHeader>
          <div className="mt-2">
            {editingVehicle && (
              <VehicleForm
                initial={editingVehicle}
                onSubmit={handleEdit}
                submitting={submitting}
                onDeleteExisting={(path) => handleDeletePhoto(editingVehicle._id, path)}
                onSetCoverExisting={(path) => handleSetCover(editingVehicle._id, path)}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Gallery lightbox */}
      {galleryVehicle && (() => {
        const photos = galleryVehicle.photos && galleryVehicle.photos.length ? galleryVehicle.photos : (galleryVehicle.photo_path ? [galleryVehicle.photo_path] : []);
        if (!photos.length) return null;
        const safeIdx = Math.min(galleryIdx, photos.length - 1);
        const current = photos[safeIdx];
        return (
          <div
            className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4"
            onClick={() => setGalleryVehicle(null)}
            data-testid="garage-gallery-lightbox"
          >
            <button
              type="button"
              className="absolute top-4 right-4 text-white hover:text-emerald-400 p-2"
              onClick={(e) => { e.stopPropagation(); setGalleryVehicle(null); }}
              data-testid="gallery-close"
            >
              <X className="w-7 h-7" />
            </button>
            <div className="absolute top-4 left-4 text-white text-sm font-semibold bg-black/40 px-3 py-1.5 rounded-full">
              {galleryVehicle.brand} {galleryVehicle.model} · {safeIdx + 1}/{photos.length}
            </div>
            {photos.length > 1 && (
              <>
                <button
                  type="button"
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full"
                  onClick={(e) => { e.stopPropagation(); setGalleryIdx((safeIdx - 1 + photos.length) % photos.length); }}
                  data-testid="gallery-prev"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  type="button"
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full"
                  onClick={(e) => { e.stopPropagation(); setGalleryIdx((safeIdx + 1) % photos.length); }}
                  data-testid="gallery-next"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
            <img
              src={getPhotoUrl(current)}
              alt=""
              className="max-h-[85vh] max-w-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
              data-testid="gallery-image"
            />
            {photos.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-black/40 p-2 rounded-xl max-w-[90vw] overflow-x-auto">
                {photos.map((p, i) => (
                  <button
                    key={p}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setGalleryIdx(i); }}
                    className={`w-14 h-14 rounded overflow-hidden border-2 shrink-0 ${i === safeIdx ? 'border-emerald-400' : 'border-transparent'}`}
                  >
                    <img src={getPhotoUrl(p)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
